import React, { FC, useState, KeyboardEvent } from "react";
import styled from "@emotion/styled";
import { Text } from "@radix-ui/themes";
import { Button } from "@/components/button";
import { TERMINAL_FONT_FAMILY } from "./shared/constants";
import { ErrorContainer } from "./shared/styles";
import { SearchableItemSelector } from "./SearchableItemSelector";

interface SelectedItem {
  id: string;
  kind: "NOTE" | "COLLECTION";
  label: string;
}

interface ChatInputProps {
  onSendMessage: (message: string, contexts?: SelectedItem[]) => Promise<void>;
  onOpenAdvanced?: () => void;
  isSubmitting?: boolean;
  placeholder?: string;
}

export const ChatInput: FC<ChatInputProps> = ({
  onSendMessage,
  onOpenAdvanced,
  isSubmitting = false,
  placeholder = "> Send a message...",
}) => {
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);

  const handleSend = async () => {
    if (!message.trim() || isSubmitting) return;

    setError(null);
    try {
      await onSendMessage(message.trim(), selectedItems.length > 0 ? selectedItems : undefined);
      setMessage("");
      setSelectedItems([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send message");
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Container>
      <ContextSelector>
        <SearchableItemSelector
          selectedItems={selectedItems}
          onItemsChange={setSelectedItems}
          disabled={isSubmitting}
          placeholder="🔍 Mention notes or collections..."
        />
      </ContextSelector>

      <InputContainer>
        <MessageInput
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          disabled={isSubmitting}
          rows={2}
        />
        <InputActions>
          <Button size="2" onClick={handleSend} disabled={!message.trim() || isSubmitting}>
            {isSubmitting ? "Sending..." : "Send"}
          </Button>
        </InputActions>
      </InputContainer>

      {error && (
        <ErrorContainer>
          <Text color="red" size="1">
            {error}
          </Text>
        </ErrorContainer>
      )}

      <HelpText>
        <Text size="1" color="gray">
          Enter to send, Shift+Enter for new line.
        </Text>
      </HelpText>

      {onOpenAdvanced && (
        <AdvancedButton size="1" variant="ghost" onClick={onOpenAdvanced} disabled={isSubmitting}>
          Send JSON Event ⚙️
        </AdvancedButton>
      )}
    </Container>
  );
};

const Container = styled.div`
  border-top: 1px solid var(--gray-6);
  padding: 12px 0;
  font-family: ${TERMINAL_FONT_FAMILY};
`;

const ContextSelector = styled.div`
  margin-bottom: 12px;
`;

const InputContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const MessageInput = styled.textarea`
  flex: 1;
  resize: none;
  font-size: 13px;
  font-family: ${TERMINAL_FONT_FAMILY};
  padding: 8px 12px;
  border: 1px solid var(--gray-6);
  background-color: transparent;
  color: var(--gray-12);
  outline: none;
  line-height: 1.4;

  &:focus {
    border-color: var(--gray-8);
    outline: 1px solid var(--gray-8);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::placeholder {
    color: var(--gray-9);
  }
`;

const InputActions = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const AdvancedButton = styled(Button)`
  font-size: 11px;
  text-decoration: underline;
  margin-top: 2px;
  padding: 0px 6px;
`;

const HelpText = styled.div`
  margin-top: 8px;
  font-size: 13px;
  color: var(--gray-10);
  font-family: ${TERMINAL_FONT_FAMILY};
`;
